import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PLANS } from '../../config/plans';
import { AI_DAILY_LIMITS } from '../../config/aiLimits';
import UpgradeButton from './UpgradeButton';
import PlanFeatureCompare from './PlanFeatureCompare';
import './PlanBillingPanel.css';

export default function PlanBillingPanel({ usage, currentPlan }) {
  const [yearly, setYearly] = useState(false);
  const plan = (currentPlan || usage?.plan || 'FREE').toUpperCase();
  const aiRemaining = usage?.aiRemaining ?? 0;
  const aiLimit = usage?.aiDailyLimit ?? 0;
  const aiExhausted = usage && aiLimit > 0 && aiRemaining <= 0;
  const aiRunningLow = usage && aiRemaining > 0 && aiRemaining <= 2;

  return (
    <div className="plan-billing-panel">
      <div className="plan-billing-summary">
        <p>
          <strong>Current plan:</strong>{' '}
          <span className={`plan-billing-badge plan-billing-badge--${plan.toLowerCase()}`}>{plan}</span>
        </p>
        {usage && (
          <p className={`plan-billing-usage${aiExhausted ? ' plan-billing-usage--exhausted' : ''}`}>
            AI today: {usage.aiUsedToday} / {usage.aiDailyLimit} requests ({usage.aiRemaining} remaining)
          </p>
        )}
      </div>

      {aiExhausted && (
        <div className="plan-billing-ai-alert" role="status">
          <p className="plan-billing-ai-alert__headline">Your AI credits for today are used up</p>
          <p className="plan-billing-ai-alert__body">
            {plan === 'FREE' ? (
              <>
                Free includes {aiLimit} AI requests per day — explain, mind maps, and related topics pause until
                your quota resets. Upgrade to Pro for {AI_DAILY_LIMITS.pro} requests/day, mind map AI search, and
                more room to study with AI.
              </>
            ) : (
              <>
                You have reached today&apos;s {plan} AI cap ({aiLimit} requests). Credits reset tomorrow, or contact
                us if you need a higher limit for your team.
              </>
            )}
          </p>
        </div>
      )}

      {aiRunningLow && !aiExhausted && (
        <p className="plan-billing-ai-alert plan-billing-ai-alert--low" role="status">
          Only {aiRemaining} AI {aiRemaining === 1 ? 'request' : 'requests'} left today
          {plan === 'FREE' ? ' — upgrade to Pro for a higher daily cap.' : '.'}
        </p>
      )}

      {plan === 'PRO' || plan === 'TEAM' ? (
        <p className="plan-billing-note">You are on a paid plan. Manage billing below or compare team options.</p>
      ) : (
        <>
          <h3 className="plan-billing-heading">
            {aiExhausted ? 'Keep learning with AI — upgrade to Pro' : 'Upgrade to Pro'}
          </h3>
          <p className="plan-billing-sub">
            {aiExhausted
              ? 'Unlock more daily AI credits, mind map search, and power-user analytics.'
              : 'Choose monthly or yearly billing, then start secure checkout.'}
          </p>
          <div className="plan-billing-toggle">
            <button type="button" className={!yearly ? 'active' : ''} onClick={() => setYearly(false)}>
              Monthly
            </button>
            <button type="button" className={yearly ? 'active' : ''} onClick={() => setYearly(true)}>
              Yearly (save ~17%)
            </button>
          </div>
          <div className="plan-billing-grid">
            {PLANS.map((p) => (
              <div
                key={p.id}
                className={`plan-billing-card ${p.highlight ? 'plan-billing-card--highlight' : ''} ${
                  p.id === 'free' && plan === 'FREE' ? 'plan-billing-card--current' : ''
                } ${p.id === 'pro' && plan === 'PRO' ? 'plan-billing-card--current' : ''}`}
              >
                <h4>{p.name}</h4>
                <p className="plan-billing-card-tagline">{p.tagline}</p>
                <p className="plan-billing-price">
                  ${yearly ? p.yearly : p.monthly}
                  <span>/{yearly ? 'year' : 'mo'}</span>
                </p>
                <ul>
                  {p.features.slice(0, 4).map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
                {p.checkout && plan !== 'PRO' ? (
                  <>
                    <p className="plan-billing-onclick-hint">{p.upgradeNote}</p>
                    <UpgradeButton plan={p.planKey || 'pro'} interval={yearly ? 'yearly' : 'monthly'} useCheckoutPage>
                      Go to checkout
                    </UpgradeButton>
                  </>
                ) : p.id === 'free' ? (
                  <span className="plan-billing-current-label">Current plan</span>
                ) : (
                  <>
                    <p className="plan-billing-onclick-hint">{p.upgradeNote}</p>
                    <Link to="/contact" className="plan-billing-link-btn">
                      {p.cta}
                    </Link>
                  </>
                )}
              </div>
            ))}
          </div>
          <PlanFeatureCompare />
        </>
      )}

      {(plan === 'PRO' || plan === 'TEAM') && <PlanFeatureCompare selectedPlanId={plan.toLowerCase()} />}

      <p className="plan-billing-footer">
        Pro unlocks higher AI limits and priority features. Questions?{' '}
        <Link to="/contact">Contact sales</Link> for Team.
      </p>
    </div>
  );
}
